import type { LoginDiagnosticStage } from "./login-diagnostics";
import type { BrowserHelpReason } from "./trusted-device-fallback.ts";

const MAX_SERVER_MESSAGE_LENGTH = 200;
const CLIENT_REJECTION_MIN_STATUS = 400;
const SERVER_FAILURE_MIN_STATUS = 500;

const MULTILINE_PATTERN = /[\n\r\t]/;

const TRANSPORT_MESSAGE_PATTERN =
  /^(network error|failed to fetch|load failed|request (failed|aborted)|timeout of \d+ms exceeded|cancell?ed|socket hang up|econnaborted|econnrefused|econnreset|etimedout|enotfound|enetunreach|ehostunreach|err_[a-z_]+)\b/i;

const INTERNAL_DETAIL_PATTERN =
  /(https?:\/\/|wss?:\/\/|\bat\s+\S+:\d+|[A-Za-z0-9_-]{32,})/;

// Blocks a credential only when it is followed by its value; plain prose such
// as "Invalid credentials" stays readable for the student.
const CREDENTIAL_SHAPE_PATTERN =
  /\b(access[_-]?token|refresh[_-]?token|api[_-]?key|authorization|bearer|challenge|challenge[_-]?token|cookie|credential|otp|passcode|password|private[_-]?key|proof|public[_-]?key|secret|signature|token)\b\s*["']?\s*[:=]/i;

/**
 * Translation key of the student-facing alert shown after a failed login.
 *
 * @example
 * const key: LoginFailureTranslationKey = "auth.loginServerUnreachable";
 * errorToast(trans(key));
 */
export type LoginFailureTranslationKey =
  | "auth.loginBrowserUnsupported"
  | "auth.loginInsecureConnection"
  | "auth.loginOpenInExternalBrowser"
  | "auth.loginPrivateModeBlocked"
  | "auth.loginDeviceStorageBlocked"
  | "auth.loginDeviceChallengeTimedOut"
  | "auth.loginDeviceTimedOut"
  | "auth.loginFailed"
  | "auth.loginServerUnreachable"
  | "auth.loginUnexpected";

/**
 * Everything known about a login failure that may change what a student sees.
 *
 * @example
 * const context: LoginFailureContext = {
 *   stage: "loginAction",
 *   responseStatus: 403,
 *   serverMessage: "This device could not be verified. Please try again.",
 * };
 */
export type LoginFailureContext = {
  stage: LoginDiagnosticStage;
  responseStatus?: number;
  serverMessage?: string;
  timedOut?: boolean;
  /** Set when every trusted-device rung failed in this browser. */
  browserHelp?: BrowserHelpReason;
};

/**
 * Alert to render — a vetted backend message when one exists, else a key.
 *
 * @example
 * const alert: LoginFailureAlert = { translationKey: "auth.loginFailed" };
 * errorToast(alert.serverMessage || trans(alert.translationKey));
 */
export type LoginFailureAlert = {
  translationKey: LoginFailureTranslationKey;
  serverMessage?: string;
};

/**
 * Turn a diagnosed login failure into the one alert a student can act on.
 *
 * @remarks
 * The mapping is total over every {@link LoginDiagnosticStage} so an unhandled
 * step can never fall through to a blank toast. A backend message is forwarded
 * only for a deliberate client-side rejection that passes every safety check —
 * raw transport text, stack traces, endpoint URLs, and credential-shaped
 * fragments are dropped in favour of a translated key.
 *
 * @example
 * const alert = resolveLoginFailureAlert({
 *   stage: "trustedDeviceDatabase",
 * });
 * // → { translationKey: "auth.loginDeviceStorageBlocked" }
 */
export function resolveLoginFailureAlert(
  context: LoginFailureContext,
): LoginFailureAlert {
  if (context.timedOut) {
    return resolveTimeoutAlert(context.stage);
  }

  if (context.browserHelp) {
    return resolveBrowserHelpAlert(context.browserHelp);
  }

  switch (context.stage) {
    case "trustedDeviceCapability":
    case "trustedDeviceSignature":
      return { translationKey: "auth.loginBrowserUnsupported" };

    case "trustedDeviceDatabase":
      return { translationKey: "auth.loginDeviceStorageBlocked" };

    case "trustedDeviceChallenge":
      return { translationKey: "auth.loginServerUnreachable" };

    case "loginAction":
      return resolveLoginActionAlert(context);

    case "authRecovery":
    case "loginCompletion":
    default:
      return { translationKey: "auth.loginUnexpected" };
  }
}

/**
 * Specific advice once no trusted-device rung (key, JWK, cookie) can work.
 */
function resolveBrowserHelpAlert(reason: BrowserHelpReason): LoginFailureAlert {
  switch (reason) {
    case "insecureConnection":
      return { translationKey: "auth.loginInsecureConnection" };
    case "inAppBrowser":
      return { translationKey: "auth.loginOpenInExternalBrowser" };
    case "privateMode":
      return { translationKey: "auth.loginPrivateModeBlocked" };
    case "unsupported":
    default:
      return { translationKey: "auth.loginBrowserUnsupported" };
  }
}

// Only a local storage stall is fixed by closing other tabs; a slow backend
// challenge is a connection or server delay.
function resolveTimeoutAlert(stage: LoginDiagnosticStage): LoginFailureAlert {
  if (stage === "trustedDeviceChallenge") {
    return { translationKey: "auth.loginDeviceChallengeTimedOut" };
  }

  return { translationKey: "auth.loginDeviceTimedOut" };
}

function resolveLoginActionAlert(
  context: LoginFailureContext,
): LoginFailureAlert {
  const { responseStatus } = context;

  if (isClientRejection(responseStatus)) {
    const serverMessage = getSafeServerMessage(context.serverMessage);

    if (serverMessage) {
      return { translationKey: "auth.loginFailed", serverMessage };
    }

    return { translationKey: "auth.loginFailed" };
  }

  if (isServerUnreachable(responseStatus)) {
    return { translationKey: "auth.loginServerUnreachable" };
  }

  return { translationKey: "auth.loginUnexpected" };
}

function isClientRejection(responseStatus: number | undefined): boolean {
  if (typeof responseStatus !== "number") return false;

  return (
    responseStatus >= CLIENT_REJECTION_MIN_STATUS &&
    responseStatus < SERVER_FAILURE_MIN_STATUS
  );
}

function isServerUnreachable(responseStatus: number | undefined): boolean {
  if (typeof responseStatus !== "number") return true;

  return responseStatus === 0 || responseStatus >= SERVER_FAILURE_MIN_STATUS;
}

function getSafeServerMessage(value: string | undefined): string | undefined {
  if (typeof value !== "string") return undefined;

  const message = value.trim();

  if (message.length === 0) return undefined;

  if (message.length > MAX_SERVER_MESSAGE_LENGTH) return undefined;

  if (MULTILINE_PATTERN.test(message)) return undefined;

  if (TRANSPORT_MESSAGE_PATTERN.test(message)) return undefined;

  if (INTERNAL_DETAIL_PATTERN.test(message)) return undefined;

  if (CREDENTIAL_SHAPE_PATTERN.test(message)) return undefined;

  return message;
}
